import type {
    NasModel,
    NasRun,
    NasRunVersion
} from "../../scripts/react/components/Models/NAS/types";

interface NasRunCache {
    run: NasRun;
    fetchedAt: number;
}

export interface NasRunsState {
    // Cache of NAS runs by datasetId -> versionId -> run data
    nasRunsCache: Record<string, Record<string, NasRunCache>>;

    // Loading states
    nasRunsLoading: Record<string, boolean>;

    // Error states
    nasRunsErrors: Record<string, string | null>;

    // Actions
    setNasRun: (datasetId: string, versionId: string, run: NasRun) => void;
    getNasRun: (datasetId: string, versionId: string) => NasRun | null;
    getNasRunModel: (datasetId: string, versionId: string, modelUrl: string) => NasModel | null;
    setNasRunLoading: (datasetId: string, versionId: string, loading: boolean) => void;
    setNasRunError: (datasetId: string, versionId: string, error: string | null) => void;
    isNasRunLoading: (datasetId: string, versionId: string) => boolean;
    clearNasRunsCache: (datasetId?: string) => void;
    updateNasModelFavorite: (
        datasetId: string,
        versionId: string,
        modelId: string,
        userId: string,
        favoriteStatus: boolean
    ) => void;
}

// Cache TTL: 5 minutes
const CACHE_TTL_MS = 5 * 60 * 1000;

export function buildNasRunsStore(set, get): NasRunsState {
    return {
        nasRunsCache: {},
        nasRunsLoading: {},
        nasRunsErrors: {},

        setNasRun(datasetId: string, versionId: string, run: NasRun) {
            set(
                (state) => ({
                    nasRunsCache: {
                        ...state.nasRunsCache,
                        [datasetId]: {
                            ...state.nasRunsCache[datasetId],
                            [versionId]: { run, fetchedAt: Date.now() }
                        }
                    }
                }),
                false,
                "nasRunsCacheSet"
            );
        },

        getNasRun(datasetId: string, versionId: string): NasRun | null {
            const cache = get().nasRunsCache[datasetId]?.[versionId];
            if (!cache) return null;

            // Check if cache is still valid
            if (Date.now() - cache.fetchedAt > CACHE_TTL_MS) {
                return null;
            }

            return cache.run;
        },

        getNasRunModel(datasetId: string, versionId: string, modelUrl: string): NasModel | null {
            const run = get().getNasRun(datasetId, versionId);
            if (!run) return null;
            return run.models.find((m) => m.url === modelUrl) || null;
        },

        setNasRunLoading(datasetId: string, versionId: string, loading: boolean) {
            const cacheKey = `${datasetId}:${versionId}`;
            set(
                (state) => ({ nasRunsLoading: { ...state.nasRunsLoading, [cacheKey]: loading } }),
                false,
                loading ? "nasRunsLoading" : "nasRunsLoadingDone"
            );
        },

        setNasRunError(datasetId: string, versionId: string, error: string | null) {
            const cacheKey = `${datasetId}:${versionId}`;
            set(
                (state) => ({ nasRunsErrors: { ...state.nasRunsErrors, [cacheKey]: error } }),
                false,
                error ? "nasRunsError" : "nasRunsErrorClear"
            );
        },

        isNasRunLoading(datasetId: string, versionId: string): boolean {
            const cacheKey = `${datasetId}:${versionId}`;
            return get().nasRunsLoading[cacheKey] || false;
        },

        clearNasRunsCache(datasetId?: string) {
            if (datasetId) {
                set(
                    (state) => ({ nasRunsCache: { ...state.nasRunsCache, [datasetId]: {} } }),
                    false,
                    "nasRunsCacheClear"
                );
            } else {
                set({ nasRunsCache: {} }, false, "nasRunsCacheClearAll");
            }
        },

        updateNasModelFavorite(
            datasetId: string,
            versionId: string,
            modelId: string,
            userId: string,
            favoriteStatus: boolean
        ) {
            const cache = get().nasRunsCache[datasetId]?.[versionId];
            if (!cache) return;

            const updatedModels = cache.run.models.map((model) => {
                if (model.id === modelId) {
                    return {
                        ...model,
                        favorites: { ...model.favorites, [userId]: favoriteStatus }
                    };
                }
                return model;
            });

            set(
                (state) => ({
                    nasRunsCache: {
                        ...state.nasRunsCache,
                        [datasetId]: {
                            ...state.nasRunsCache[datasetId],
                            [versionId]: { ...cache, run: { ...cache.run, models: updatedModels } }
                        }
                    }
                }),
                false,
                "nasModelFavoriteUpdate"
            );
        }
    };
}
